/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsdoc/require-jsdoc */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeSeriesScale
} from 'chart.js';
import { ReactElement, forwardRef, useCallback, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  customAxisTitlePlugin,
  dynamicLineColorPlugin,
  pinnedTooltipsPlugin,
  textPointPlugin
} from '../chartPlugins';
import {
  ClearPinsButton,
  usePinnedTooltips,
  withPinnedTooltips
} from '../hooks/usePinnedTooltips';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  zoomPlugin,
  TimeSeriesScale,
  textPointPlugin,
  dynamicLineColorPlugin,
  customAxisTitlePlugin,
  {
    id: 'uniqueid5',
    afterDraw(chart: any) {
      if (!chart.tooltip) return;
      // This global plugin draws a vertical crosshair; skip it on non-cartesian
      // charts (doughnut/pie/gauge) which have no Y scale.
      if (!chart.scales?.y) return;
      // eslint-disable-next-line no-underscore-dangle
      if (chart.tooltip._active && chart.tooltip?._active?.length) {
        const activePoint = chart.tooltip?._active[0];
        const { ctx } = chart;
        const { x } = activePoint.element;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#475467';
        ctx.stroke();
        ctx.restore();
      }
    }
  }
);
type props = {
  data: any;
  options: any;
  /** Set false to turn click-to-pin tooltips off for this chart. */
  enablePinnedTooltips?: boolean;
  /** Font size of the pinned tooltip boxes. */
  pinnedTooltipFontSize?: number;
};

/**
 * Graph component that renders a line chart using Chart.js and React-Chartjs-2.
 * Clicking a data point pins its tooltip to the chart so several points can be
 * read at once; clicking it again (or "Clear pins") removes it.
 * @param props - The component props.
 * @param props.options - Options property of linechart.
 * @param props.data - Data for thr line chart.
 * @param props.enablePinnedTooltips - Turns click-to-pin tooltips off.
 * @param props.pinnedTooltipFontSize - Font size of the pinned boxes.
 * @param ref - Forwarded ref to access the Chart.js instance.
 * @returns  A React element representing the Graph component.
 */
const LineChart = forwardRef<any, props>(
  (
    { options, data, enablePinnedTooltips = true, pinnedTooltipFontSize },
    ref
  ): ReactElement => {
    const chartRef = useRef<any>(null);
    const pinned = usePinnedTooltips({
      chartRef,
      resetKey: data?.datasets,
      fontSize: pinnedTooltipFontSize,
      enabled: enablePinnedTooltips
    });

    // Options are only rebuilt when the caller's own options change: a pin
    // toggle must not replace them, or charts with zoom/pan would snap back.
    const chartOptions = useMemo(
      () => (enablePinnedTooltips ? withPinnedTooltips(options, pinned) : options),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [options, enablePinnedTooltips, pinned.onChartClick, pinned.pluginOptions]
    );

    // Keep our own handle on the chart while still honouring the caller's ref.
    const setChartRef = useCallback(
      (instance: any) => {
        chartRef.current = instance;
        if (typeof ref === 'function') ref(instance);
        else if (ref) ref.current = instance;
      },
      [ref]
    );

    return (
      <div className="relative text-gray-500 h-full w-full flex flex-1">
        <Line
          ref={setChartRef}
          options={chartOptions}
          data={data}
          plugins={[pinnedTooltipsPlugin]}
        />
        {enablePinnedTooltips && pinned.pins.length > 0 && (
          <div className="absolute right-2 top-2 z-10">
            <ClearPinsButton count={pinned.pins.length} onClear={pinned.clear} />
          </div>
        )}
      </div>
    );
  }
);

LineChart.displayName = 'LineChart';

export default LineChart;
